import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getDirectorOrderedVideos, updateDirectorVideoOrder, updateVideo } from './api'; // Assuming updateVideo is for PUT /api/videos/:id

export default function DirectorVideoOrderPanel({ directorId }) {
  const { token } = useOutletContext();
  const [directorVideos, setDirectorVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDirectorVideos = useCallback(async () => {
    if (!directorId || !token) {
      setDirectorVideos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const videos = await getDirectorOrderedVideos(directorId, token);
      // Ensure videos are sorted by display_order, though API should handle this
      const sortedVideos = [...videos].sort((a, b) => a.display_order - b.display_order);
      console.log('[DirectorVideoOrderPanel] Fetched and sorted directorVideos:', JSON.parse(JSON.stringify(sortedVideos))); // Log a deep copy
      setDirectorVideos(sortedVideos);
    } catch (err) {
      console.error(`Error fetching videos for director ${directorId}:`, err);
      setError(`Failed to load videos: ${err.message || 'Unknown error'}`);
      setDirectorVideos([]); // Clear videos on error
    } finally {
      setLoading(false);
    }
  }, [directorId, token]);

  useEffect(() => {
    fetchDirectorVideos();
  }, [fetchDirectorVideos]);

  const handleToggleActive = async (videoId, currentActiveStatus) => {
    // TODO: Implement optimistic update and error handling
    console.log(`Toggling active for video ${videoId}, current: ${currentActiveStatus}`);
    try {
      setLoading(true);
      await updateVideo(videoId, { active: !currentActiveStatus }, token);
      await fetchDirectorVideos(); // Refetch to get updated list and order
    } catch (err) {
      console.error('Error toggling video active state:', err);
      setError(`Failed to toggle active state: ${err.message}`);
      // Optionally refetch even on error to ensure UI consistency
      await fetchDirectorVideos(); 
    }
  };

  const moveVideo = async (currentIndex, direction) => {
    console.log(`[DirectorVideoOrderPanel] moveVideo called. Index: ${currentIndex}, Direction: ${direction}`);
    const newIndex = currentIndex + direction;
    if (newIndex < 0 || newIndex >= directorVideos.length) {
      console.log('[DirectorVideoOrderPanel] moveVideo: New index out of bounds.');
      return;
    }

    // Deep clone current videos to avoid direct state mutation issues before splice
    let currentItemsClone = JSON.parse(JSON.stringify(directorVideos));
    const originalMovedItem = currentItemsClone[currentIndex]; // Get the item before splicing from clone
    
    console.log('[DirectorVideoOrderPanel] Original item to be moved:', JSON.parse(JSON.stringify(originalMovedItem)));

    // Perform splice on a new array derived from state for the optimistic update
    const itemsForOptimisticUpdate = [...directorVideos];
    const [splicedItem] = itemsForOptimisticUpdate.splice(currentIndex, 1);
    itemsForOptimisticUpdate.splice(newIndex, 0, splicedItem);

    // Create the new state for optimistic update, ensuring display_order is re-calculated
    const newOptimisticState = itemsForOptimisticUpdate.map((item, idx) => ({ ...item, display_order: idx + 1 }));
    console.log('[DirectorVideoOrderPanel] Optimistic UI update - new state:', JSON.parse(JSON.stringify(newOptimisticState)));
    setDirectorVideos(newOptimisticState);

    // Find the item in the *new optimistically ordered* list that corresponds to the one we moved.
    // Its dvo_id should be the same as originalMovedItem.dvo_id.
    // Its display_order will be its new optimistic display_order (newIndex + 1).
    const itemForApiPayload = newOptimisticState.find(item => item.dvo_id === originalMovedItem.dvo_id);
    
    console.log('[DirectorVideoOrderPanel] Item identified for API payload (from optimistic state):', JSON.parse(JSON.stringify(itemForApiPayload)));

    if (itemForApiPayload && typeof itemForApiPayload.dvo_id !== 'undefined') {
      try {
        setLoading(true);
        console.log(`[DirectorVideoOrderPanel] Calling updateDirectorVideoOrder with dvoId: ${itemForApiPayload.dvo_id}, payload: { new_display_order: ${itemForApiPayload.display_order} }`);
        await updateDirectorVideoOrder(itemForApiPayload.dvo_id, { new_display_order: itemForApiPayload.display_order }, token);
        console.log('[DirectorVideoOrderPanel] updateDirectorVideoOrder API call finished.');
        // Temporarily, we might not refetch immediately to see if optimistic update holds visually
        // await fetchDirectorVideos(); 
      } catch (err) {
        console.error('[DirectorVideoOrderPanel] Error reordering director videos:', err);
        setError(`Failed to reorder videos: ${err.message}`);
        // In case of error, definitely refetch to revert to server state
        // await fetchDirectorVideos(); 
      } finally {
        setLoading(false); // Ensure loading is set to false
        // ALWAYS refetch after API attempt (success or fail) to ensure UI reflects true server state.
        console.log('[DirectorVideoOrderPanel] Refetching director videos post-API call.');
        await fetchDirectorVideos(); 
      }
    } else {
      console.warn('[DirectorVideoOrderPanel] Could not find itemForApiPayload or dvo_id is missing. Original moved item:', JSON.parse(JSON.stringify(originalMovedItem)), 'Optimistic list:', JSON.parse(JSON.stringify(newOptimisticState)));
      // If we can't make an API call, still refetch to be safe, though state might be weird.
      await fetchDirectorVideos(); 
    }
  };

  if (!directorId) {
    return <div style={{color: 'white', margin: '20px'}}>Please select a director to see their video order.</div>;
  }

  return (
    <div style={{ margin: '32px 0', color: 'white' }}>
      <h4>Video Order for Director ID: {directorId}</h4>
      {loading && <div>Loading videos...</div>}
      {error && <div style={{ color: 'red', margin: '10px 0' }}>{error}</div>}
      
      {!loading && directorVideos.length === 0 && !error && (
        <div>No videos found for this director in the display order.</div>
      )}

      <div>
        {directorVideos.map((video, index) => (
          <div 
            key={video.dvo_id || video.id} // Use dvo_id if available, fallback to video.id
            style={{
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '10px',
              padding: '10px',
              backgroundColor: 'transparent',
              borderBottom: '1px solid #eee',
              borderRadius: '4px'
            }}
          >
            <div style={{ width: '30px', textAlign: 'center', fontWeight: 'bold', marginRight: '10px' }}>
              {video.display_order}.
            </div>
            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', marginRight: '10px' }}>
              {video.title} (ID: {video.id}, DVO_ID: {video.dvo_id})
            </div>
            <div style={{ marginRight: '10px' }}>
              <button 
                onClick={() => handleToggleActive(video.id, video.active)}
                disabled={loading}
                style={{ padding: '4px 8px', backgroundColor: video.active ? '#FF6347' : '#4CAF50', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                {video.active ? 'Set Inactive' : 'Set Active'}
              </button>
            </div>
            <div>
              <button 
                onClick={() => moveVideo(index, -1)} 
                disabled={index === 0 || loading}
                style={{ margin: '0 2px', padding: '2px 8px', backgroundColor: (index === 0 || loading) ? '#cccccc' : '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                &#9650; {/* Up Arrow */}
              </button>
              <button 
                onClick={() => moveVideo(index, 1)} 
                disabled={index === directorVideos.length - 1 || loading}
                style={{ margin: '0 2px', padding: '2px 8px', backgroundColor: (index === directorVideos.length - 1 || loading) ? '#cccccc' : '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                &#9660; {/* Down Arrow */}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
